<template>
    <section class="card meta-section" v-if="WebProfileStore.isAdmin">
        <div class="section-title-row">
            <h1 class="section-title">
                {{
                    $trans("web_profile.meta_description") || "Meta Description"
                }}
            </h1>

            <div class="button-group">
                <el-button
                    type="primary"
                    size="small"
                    :loading="isGenerating"
                    :disabled="isGenerating"
                    @click="generateAndSaveMeta"
                    class="generate-btn"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        class="mr-2"
                    >
                        <path
                            d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"
                        />
                    </svg>
                    {{ $trans("web_profile.generate_meta") || "AI Generate" }}
                </el-button>

                <EditButton :onClick="WebProfileStore.openEditMetaModal" />
            </div>
        </div>

        <div class="details-header">
            <div class="meta-content">
                <div
                    v-if="
                        WebProfileStore.profile.meta_description ||
                        !isGenerating
                    "
                    class="meta-text"
                    style="white-space: pre-wrap"
                >
                    <span v-if="WebProfileStore.profile.meta_description">
                        {{ WebProfileStore.profile.meta_description }}
                    </span>
                    <span v-else class="empty-text">
                        {{
                            $trans("web_profile.add_meta") ||
                            "Add Meta Description"
                        }}
                    </span>
                </div>

                <el-skeleton
                    v-if="isGenerating && !WebProfileStore.profile.meta_description"
                    animated
                    :rows="2"
                    style="width: 100%"
                />
            </div>
        </div>

        <EditMetaModal />
    </section>
</template>

<script>
import { mapStores } from "pinia";
import { useWebProfileStore } from "@/stores/WebProfileStore";
import EditButton from "./../Components/EditButton.vue";
import EditMetaModal from "./Modals/EditMetaModal.vue";

export default {
    name: "MetaSection",
    components: {
        EditButton,
        EditMetaModal,
    },
    data() {
        return {
            isGenerating: false,
        };
    },
    computed: {
        ...mapStores(useWebProfileStore),
        hasSummary() {
            const summary = this.WebProfileStore.profile?.summary;
            return typeof summary === "string" && summary.trim().length > 0;
        },
    },
    methods: {
        async generateAndSaveMeta() {
            if (this.isGenerating) return;

            if (!this.hasSummary) {
                this.$message.error(
                    this.$trans("web_profile.generate_summary_first") ||
                        "You must generate Summary first.",
                );
                return;
            }

            this.isGenerating = true;

            const oldMetaDescription = this.WebProfileStore.profile.meta_description;
            this.WebProfileStore.profile.meta_description = "";

            try {
                const profileId = this.WebProfileStore.profile.id;
                if (!profileId) return;


                const response = await fetch(
                    `/candidates/${profileId}/web-profile/generate/meta`,
                );

                if (!response.ok) {
                    this.WebProfileStore.profile.meta_description = oldMetaDescription;
                    if (response.status === 429) {
                        this.$message.warning(
                            this.$trans("web_profile.ai_busy") ||
                                "AI is busy, please try again later.",
                        );
                        return;
                    } else {
                        this.$message.error("AI server error");
                        return;
                    }
                }

                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                let fullContent = "";

                while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;

                    const chunk = decoder.decode(value, { stream: true });
                    fullContent += chunk;

                    this.WebProfileStore.profile.meta_description = fullContent;
                }

                if (fullContent && this.WebProfileStore.updateMeta) {
                    await this.WebProfileStore.updateMeta(fullContent.trim());
                }
            } catch (error) {
                console.error("Error generating meta stream:", error);
            } finally {
                this.isGenerating = false;
            }
        },
    },
};
</script>

<style scoped>
.meta-section {
    padding: 1.5rem;
    border: 1px solid #ddd;
    border-radius: 8px;
    background-color: #fff;
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.section-title-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;
}

.section-title {
    margin: 0;
    font-size: 1.875rem;
    font-weight: 600;
}

.details-header {
    display: flex;
    width: 100%;
}

.meta-content {
    flex: 1;
}

.meta-text {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    word-break: break-all;
}

.empty-text {
    color: #999;
    font-style: italic;
    font-size: 1.2rem;
}

.button-group {
    display: flex;
    gap: 10px;
    flex-shrink: 0;
    align-items: center;
}

.generate-btn {
    background-color: #41abc4;
    border-color: #41abc4;
    color: white;
    font-weight: 500;
    height: 34px;
    font-size: 16px;
    font-weight: 700 !important;
}

.generate-btn:hover {
    background-color: #3693a9;
    border-color: #3693a9;
}

.generate-btn.is-disabled,
.generate-btn.is-disabled:hover,
.generate-btn.is-disabled:focus,
.generate-btn.is-disabled:active {
    background-color: #999 !important;
    border-color: #999 !important;
    cursor: not-allowed !important;
    pointer-events: auto !important;
}

.mr-2 {
    margin-right: 0.5rem;
}
</style>
